export const FEATURE_FLAGS = {
  ASKVIVO: "askvivo",
  FORECASTING: "forecasting",
  INSIGHTS: "insights",
  SUBJECTS_OVERVIEW: "subjects-overview",
  VISITS_ACTIVITIES: "visits-activities",
  BASELINE_CHARACTERISTICS: "baseline-characteristics",
  SAFETY_MEASURES: "safety-measures",
  ADVERSE_EVENTS: "adverse-events",
  DOSING_TRACKER: "dosing-tracker",
  EFFICACY_MEASURE: "efficacy-measure",
  DATA_CONNECTIONS: "data-connections",
  // eTMF / eBinders document screens. DOCUMENTS gates the trial-level Documents
  // screen + the site-profile Documents tab; PORT_DOCUMENTS gates the portfolio
  // trial-detail Documents tab. Both query eTMF tables that only exist for
  // tenants with an eBinders feed (Florence), so they stay off elsewhere.
  DOCUMENTS: "documents",
  PORT_DOCUMENTS: "port-documents",
  // Opt-in per-tab filtering of the site profile. When off (all existing
  // tenants) every site-profile tab renders as before; when on, tabs are
  // filtered/disabled by their own backing-data flags.
  SITE_TAB_GATING: "site-tab-gating",
  // Greys out the site-profile tabs whose backing data a tenant's feed does not
  // carry (Trials-adjacent Performance, Milestones, Surveys), leaving them in
  // the menu but unclickable. Distinct from SITE_TAB_GATING, which omits such
  // tabs outright: Clario's feed has the tabs' shape but none of the content,
  // and showing them disabled says "not in your data" rather than hiding that
  // the capability exists at all.
  SITE_TAB_DISABLED: "site-tab-disabled",
  // Dashboard-only site profile: hides the Table view on the trial-level site
  // profile tabs so they open straight on the Dashboard (Timeline stays where
  // present). A tab with no dashboard content keeps its table — that is its
  // only content. Granted to the Florence trials; other tenants keep Table.
  SITE_PROFILE_DASHBOARD_ONLY: "site-profile-dashboard-only",
  // Lifecycle risk bands: site delay-risk scoring (startup) and close-out risk,
  // plus the portfolio site profile's Delay Risk tab. Separate from RISKS, which
  // owns the conduct-phase RBQM view — a tenant can have either or both, and
  // tenants with only RISKS see exactly what they saw before.
  SITE_RISK_LIFECYCLE: "site-risk-lifecycle",
  PROTOCOL_DEVIATIONS: "protocol-deviations",
  COMPONENT_NOTIFICATIONS: "component_notifications",
  COMPONENT_INSIGHTS: "component_insights",
  DATA_DISCREPANCIES: "data-discrepancies",
  // Cross-screen indicators showing that a subject/record has open
  // discrepancies (Subject Overview header, subject list, data tables).
  // Additive: when ungranted every host screen renders exactly as before.
  // The backend also has a global DISCREPANCY_MARKERS_ENABLED kill switch.
  DISCREPANCY_MARKERS: "discrepancy-markers",
  ADD_DISCREPANCY: "add-discrepancy",
  ADD_RULE: "add-rule",
  SITES_OVERVIEW: "sites-overview",
  // Hides the Metrics tab on the trial Sites screen. Unlike SITE_TAB_DISABLED,
  // which greys out side-menu entries on the portfolio site profile, Metrics is
  // a peer of Table in a horizontal view switcher, where a dead entry reads as
  // a broken control rather than as "no data for you" — so it is dropped.
  //
  // Granted to clario, where every card on the tab totals zero: apart from
  // subject_count, trial_site carries no site-level derived metrics at all —
  // screening, enrollment status, AE/SAE, SDV percent, MVR action items and
  // eCRF counts are NULL for every site.
  SITES_OVERVIEW_METRICS_HIDDEN: "sites-overview-metrics-hidden",
  // The three flags below make a profile *unreachable* rather than merely
  // sparse: they strip every link into it and block its route, so a bookmarked
  // URL lands on 404 instead of an empty screen. The list the links came from
  // is untouched — the rows still read, it is the profile behind them that has
  // nothing in it. The geographic rollups (`.../geo/...`) are also untouched:
  // they aggregate across sites and are populated when a single site is not.
  //
  // Granted to clario, whose profiles are empty by data, not by entitlement:
  // trial_site carries no site-level derived metrics, site_personnel_assignment
  // is empty, and a UUID trial_site_id breaks the GSID-prefix join that would
  // populate the rest. Three flags rather than one because the surfaces are
  // independent — a tenant can have populated investigators but empty sites.
  //
  // /portfolio/site-profiles/<siteKey>
  SITE_PROFILE_LINKS_HIDDEN: "site-profile-links-hidden",
  // /portfolio/investigators/<invKey>
  INVESTIGATOR_PROFILE_LINKS_HIDDEN: "investigator-profile-links-hidden",
  // /<scope>/<trial>/sites/profile/<siteId>/... — the trial's own site screen,
  // independent of the cross-trial rollup above.
  TRIAL_SITE_PROFILE_LINKS_HIDDEN: "trial-site-profile-links-hidden",
  SITE_MONITORING_VISITS: "site-monitoring-visits",
  CTMS_ACTION_ITEM: "ctms-action-item",
  SITE_ACTIVATION_MILESTONE: "site-activation-milestone",
  PORT_SITES_METRICS: "port-sites-metrics",
  PORT_ENROLLMENT: "port-enrollment",
  PORT_MILESTONES: "port-milestones",
  PORT_RETENTION: "port-retention",
  PORT_SAFETY: "port-safety",
  PORT_COMPLIANCE: "port-compliance",
  PORT_SITE_PROFILES: "port-site-profiles",
  PORT_INVESTIGATORS: "port-investigators",
  EXTERNAL_EVIDENCE: "external-evidence",
  LABS: "labs",
  ALERTS: "alerts",
  ROLES: "user-roles-permissions",
  CONMEDS: "conmeds",
  SCREENING: "screening",
  SCHEDULE: "schedule",
  IMAGING: "imaging",
  TIMELINE: "timeline",
  IP_ADMINISTRATION: "ip-admin",
  PHARMACOKINETICS: "pharmacokinetics",
  TRIAL_HOME_SCREEN: "trial-home",
  TRIAL_NEWS: "trial-news",
  TRIAL_NEWS_REFRESH: "trial-news-refresh",
  FILE_UPLOAD: "file-upload",
  TRIAL_ISSUES: "trial-issues",
  RISKS: "risks",
  // Top-level "Actions" nav item and its page. Previously the nav item was gated
  // by RISKS and linked into risk/tasks; Actions is now its own feature so a
  // tenant can have Actions without the rest of RBQM, or vice versa. The Tasks
  // screen under Quality stays on RISKS. This slug is NOT in the IAM catalog by
  // default, so Actions is hidden until it is granted per trial — see
  // scripts/grant-actions-flag.sql in the Vivo-Demo repo.
  ACTIONS: "actions",
  PORT_ASKVIVO: "port-askvivo",
  PORT_TRIALS: "port-trials",
  PORT_ENTER_TRIAL: "port-enter-trial",
  // Per-trial, not portfolio-wide: replaces the portfolio trial-detail Overview
  // tab with the Trial Overview briefing generated by the Role Insight Agents
  // (the same report the in-trial Trial News drawer lands on). A portfolio mixes
  // trials that carry a dataset with portfolio-only records that have nothing to
  // brief on, so this is granted per trial. The /trialnews API that serves the
  // briefing enforces TRIAL_NEWS, so grant both together — see
  // scripts/register-port-trial-summary-flag.sql in the Vivo-Demo repo.
  PORT_TRIAL_SUMMARY: "port-trial-summary",
  PORT_DATA_CONNECTIONS: "port-data-connections",
  TRIAL_PLANNING: "trial-planning",
  // Demo-only: "Add to Vivo" — publish a trial plan into Vivo as a planned
  // trial (button on the plan header + bulk action on the plans list). The
  // published record freezes the plan as of publish time, so later edits stay
  // in planning until republished. This slug is NOT in the IAM catalog by
  // default, so the buttons are hidden for every tenant until granted — see
  // scripts/grant-plan-promote-flag.sql in the Vivo-Demo repo (demo tenant only).
  PLAN_PROMOTE: "plan-promote",
  // ── Site-intelligence posture (Takeda) ────────────────────────────────────
  // Four independent flags, none of which is in the IAM catalog by default, so
  // every tenant that is not granted them renders exactly as before.
  //
  // The portfolio reads as a site-intelligence platform rather than a trial
  // one: nav ordered Ask Vivo · Sites · Investigators · Trials · Scenarios ·
  // Data, Sites as the post-login landing screen, Planning relabelled
  // "Scenarios", and the Data section listing both mastery surfaces (the
  // stewards' review queue is otherwise reachable only from a button inside
  // the analytics page).
  SITE_FIRST_NAV: "site-first-nav",
  // Trims the cross-trial site and investigator profiles to the surfaces whose
  // data those tenants actually have: no Forms / Monitoring Visits placeholders,
  // Performance ahead of the trial list, the trial list as the investigator
  // profile's table rather than cards, the investigator's site locations as
  // that same table rather than a map, and trial/category filters on the
  // milestone timeline.
  PROFILE_SIMPLIFIED: "profile-simplified",
  // Removes the Notes surface from every profile that carries one (portfolio
  // site, investigator, trial-level site, portfolio trial detail). Notes are
  // free-text visible across a tenant's whole user base, which is a
  // deliberate v1 exclusion rather than a data gap.
  NOTES_HIDDEN: "notes-hidden",
  // Reduces trial planning to a single linear scenario flow: Find Best Sites →
  // Refine Site List → Enrollment, with no plan Overview, scenario list,
  // milestone, vendor or budget steps; "Add scenario" wording; no view/group-by
  // control; profile drawers navigate in-app instead of opening a second app
  // instance; no publish (Add to Vivo); and approval reduced to a plain Save.
  PLANNING_SIMPLIFIED: "planning-simplified",
  ADMINISTRATIVE_FORM_RECORDS: "administrative-form-records",
  IP_DISPENSATION: "ip-dispensation",
  CONCOMITANT_PROCEDURES: "concomitant-procedures",
  CLARIO_IMAGING_WORKFLOW: "clario-imaging-workflow",
  // Gates ONLY the Clario "Imaging Studies" screen (nav item + route), decoupled
  // from the rest of the Clario imaging workflow (reads/operations stay on
  // CLARIO_IMAGING_WORKFLOW). No trial has this slug in the IAM catalog, so the
  // Imaging Studies screen is hidden for both Clario trials until it is granted.
  IMAGING_STUDIES: "imaging-studies",
  // Demo-specific: gates the imaging screens + the DICOM "View Images" viewer for
  // tenants with fake imaging data (e.g. DEMO_TRIAL). Kept off Clario until real
  // S3 imaging lands, so the viewer never appears there.
  IMAGING_VIEWER: "imaging-viewer",
  WAVEFORMS: "waveforms",
  // ECG as its own domain: gates the dedicated "ECG" Measures nav item, which
  // hosts the numeric interval table (ecg_intervals dataset) plus the ECG Trends
  // waveform view. Kept separate from radiology imaging so ECG no longer rides
  // the imaging_studies screens. Uses the existing IAM flag slug "ecg-measures"
  // (already enabled for CYTB323K12201) rather than a new "ecg" slug that isn't
  // in the IAM catalog — otherwise the nav item would be hidden everywhere.
  ECG: "ecg-measures",
  STUDY_DESIGN: "study-design",
  SOURCE_AUDIT: "source-audit",
  // Clario supply-chain / logistics section (StudyWorks Oracle EBS feed).
  // LOGISTICS gates the section itself; each of the nine screens then gates on
  // its own slug so a tenant only sees the datasets its feed actually carries.
  // useMenuFiltering drops a section whose children all filter out, so the
  // Logistics entry disappears on its own once every child is hidden.
  LOGISTICS: "logistics",
  LOGISTICS_MATERIALS: "logistics-materials",
  LOGISTICS_LOTS: "logistics-lots",
  LOGISTICS_LOCATIONS: "logistics-locations",
  LOGISTICS_UNITS: "logistics-units",
  LOGISTICS_SHIPMENTS: "logistics-shipments",
  LOGISTICS_SHIPMENT_ITEMS: "logistics-shipment-items",
  LOGISTICS_INVENTORY: "logistics-inventory",
  LOGISTICS_RECONCILIATION: "logistics-reconciliation",
  LOGISTICS_EVENTS: "logistics-events",
  // Phase-B oncology "Analysis" section. Each slice's child is gated by its own
  // flag; PK reuses the existing PHARMACOKINETICS slug. Granted to DEMO-ONC-2026-01.
  TUMOR_RESPONSE: "tumor-response",
  IMMUNOGENICITY: "immunogenicity",
  BIOMARKERS: "biomarkers",
  REG_DOCS_TRACKER: "reg-docs-tracker",
  SITE_FEASIBILITY_BOARD: "site-feasibility-board",
  TRIAL_ENROLLMENT: "trial-enrollment",
  TRIAL_CONTACT_LIST: "trial-contact-list",
  // Medical Review & Attestation. MEDICAL_REVIEW gates the screen, the
  // participant-profile chips, and the review endpoints; REVIEW_RULES gates
  // authoring the materiality/SLA rules in Rule Studio.
  MEDICAL_REVIEW: "medical-review",
  REVIEW_RULES: "review-rules",
  // Clinical Data Review — the peer review program for scientific-plausibility
  // domains (COA/PRO, quantitative assessments), as opposed to Medical
  // Review's safety domains. CLINICAL_DATA_REVIEW gates the screen and reads
  // (the cdr_review_items dataset, the record drawer, marks); CDR_POLICY gates
  // authoring the domains-in-scope/reviewer-roles policy, mirroring the
  // MEDICAL_REVIEW/REVIEW_RULES split above.
  CLINICAL_DATA_REVIEW: "clinical-data-review",
  CDR_POLICY: "cdr-policy",
  // Participant profile review: the profile-level sign-off (button + ledger)
  // shown on the participant profile.
  PROFILE_REVIEW: "profile-review",
  // Trial Plan of Record (in-flight). Trial Planning (TRIAL_PLANNING) is the pre-live
  // planning tool; this gates its in-flight successor, where a live trial's sponsor-owned
  // milestones and enrolment targets live with provenance, baselines and an audit trail.
  PLAN_OF_RECORD: "plan-of-record",
  // Study Document Workspace. Deliberately NOT the existing DOCUMENTS slug: that one
  // gates the read-only vendor eTMF mirror (eBinders/Florence feed), whereas this is the
  // user-upload workspace. Protocol-class upload stays disabled until V3 amendment
  // handling can resolve the effective version for Ask Vivo.
  STUDY_DOCUMENT_WORKSPACE: "study-document-workspace",
  // Definitions in Force (Data > Definitions in Force): the read-only per-trial
  // registry of governing rules/policies. Ungranted everywhere for now — pending a
  // decision on whether this becomes a broader "Rules" surface under Data.
  TRIAL_DEFINITIONS: "trial-definitions",
};
